import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import DashboardChartCard from '../../../components/common/DashboardChartCard';
import DashboardReportHeader from '../../../components/common/DashboardReportHeader';

const HALL_ACCENT = '#4ce1b6';
const SECONDARY = '#8ac9ff';
const WARNING = '#fbbf24';
const DANGER = '#fb7185';
const PURPLE = '#c4b5fd';
const PROFIT = '#34d399';
const DUE = '#f97316';

const numberFormat = new Intl.NumberFormat('en-IN');
const METHOD_COLORS = [HALL_ACCENT, SECONDARY, WARNING, PURPLE, PROFIT, DUE, DANGER, '#67e8f9'];

function MetricTile({ title, value, subtitle, icon, color, isDark, formatValue }) {
  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        minWidth: 0,
        borderRadius: '16px',
        border: `1px solid ${alpha(color, isDark ? 0.28 : 0.2)}`,
        background: isDark
          ? `linear-gradient(155deg, ${alpha('#111827', 0.96)} 0%, ${alpha('#0f172a', 0.94)} 55%, ${alpha(color, 0.12)} 100%)`
          : `linear-gradient(155deg, #ffffff 0%, #f8fafc 50%, ${alpha(color, 0.12)} 100%)`,
        boxShadow: isDark
          ? `0 12px 28px ${alpha('#000', 0.28)}`
          : `0 14px 30px ${alpha('#0f172a', 0.07)}, 0 0 0 1px ${alpha('#fff', 0.55)} inset`,
        transition: 'transform 0.22s ease, box-shadow 0.22s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isDark
            ? `0 16px 34px ${alpha('#000', 0.34)}, 0 0 0 1px ${alpha(color, 0.25)}`
            : `0 18px 36px ${alpha(color, 0.16)}`,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -30,
          right: -24,
          width: 90,
          height: 90,
          borderRadius: '50%',
          bgcolor: alpha(color, isDark ? 0.12 : 0.14),
          pointerEvents: 'none',
        }}
      />
      <Stack spacing={0.9} sx={{ p: { xs: 1.35, sm: 1.55 }, position: 'relative', zIndex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              display: 'grid',
              placeItems: 'center',
              color,
              background: `linear-gradient(145deg, ${alpha(color, 0.28)} 0%, ${alpha(color, 0.1)} 100%)`,
              border: `1px solid ${alpha(color, 0.35)}`,
              '& .MuiSvgIcon-root': { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        </Stack>
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: alpha(isDark ? '#e2e8f0' : '#475569', 0.9),
              fontWeight: 800,
              letterSpacing: '0.06em',
              lineHeight: 1.15,
              fontSize: '0.62rem',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mt: 0.2,
              fontWeight: 900,
              letterSpacing: '-0.03em',
              color: isDark ? '#f8fafc' : '#0f172a',
              fontVariantNumeric: 'tabular-nums',
              fontSize: { xs: '1.05rem', md: '1.2rem' },
              lineHeight: 1.15,
            }}
          >
            {formatValue(value)}
          </Typography>
          {subtitle ? (
            <Typography
              variant="caption"
              sx={{
                color: isDark ? alpha('#94a3b8', 0.95) : '#64748b',
                fontWeight: 600,
                display: 'block',
                mt: 0.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

/**
 * Per-hall performance analytics: compact KPI row + revenue trend + money/payment charts.
 */
export default function HallDetailAnalytics({
  hallName,
  analytics,
  isDark,
  themeMode = 'light',
  formatCurrency,
  rangeLabel,
  startDate,
  endDate,
  preset,
  onDateRangeChange,
  onRefresh,
  loading = false,
  surfaceSx = {},
}) {
  const kpis = analytics?.kpis || {};
  const charts = analytics?.charts || {};
  const chartHeight = 280;

  const metricTiles = useMemo(() => ([
    {
      key: 'events',
      title: 'Total Events',
      value: kpis.total_events ?? 0,
      subtitle: rangeLabel,
      color: HALL_ACCENT,
      icon: <EventNoteOutlinedIcon />,
      formatValue: (v) => numberFormat.format(Number(v || 0)),
    },
    {
      key: 'revenue',
      title: 'Revenue',
      value: kpis.total_revenue ?? 0,
      subtitle: rangeLabel,
      color: HALL_ACCENT,
      icon: <PaidOutlinedIcon />,
      formatValue: formatCurrency,
    },
    {
      key: 'collection',
      title: 'Collection',
      value: kpis.total_collection ?? 0,
      subtitle: 'Payments received',
      color: SECONDARY,
      icon: <AccountBalanceWalletOutlinedIcon />,
      formatValue: formatCurrency,
    },
    {
      key: 'profit',
      title: 'Profit',
      value: kpis.total_profit ?? 0,
      subtitle: 'Revenue − expenses',
      color: PROFIT,
      icon: <TrendingUpOutlinedIcon />,
      formatValue: formatCurrency,
    },
    {
      key: 'due',
      title: 'Due',
      value: kpis.total_due ?? 0,
      subtitle: 'Outstanding',
      color: DUE,
      icon: <RequestQuoteOutlinedIcon />,
      formatValue: formatCurrency,
    },
    {
      key: 'occupancy',
      title: 'Occupancy',
      value: kpis.occupancy_rate ?? 0,
      subtitle: `${kpis.occupied_days || 0} of ${kpis.range_days || 0} days`,
      color: WARNING,
      icon: <DonutLargeOutlinedIcon />,
      formatValue: (v) => `${Number(v || 0).toFixed(1)}%`,
    },
  ]), [formatCurrency, kpis, rangeLabel]);

  const moneyPie = useMemo(() => {
    const rows = charts.money_overview || [];
    const fallback = [
      { label: 'Revenue', value: Number(kpis.total_revenue || 0) },
      { label: 'Collection', value: Number(kpis.total_collection || 0) },
      { label: 'Profit', value: Number(kpis.total_profit || 0) },
      { label: 'Due', value: Number(kpis.total_due || 0) },
    ];
    const source = rows.length ? rows : fallback;
    return source.map((item, index) => ({
      name: item.label || item.name,
      value: Number(item.value || 0),
      color: [HALL_ACCENT, SECONDARY, PROFIT, DUE][index % 4],
    }));
  }, [charts.money_overview, kpis]);

  const paymentMethodPie = useMemo(() => {
    const rows = charts.payment_method_distribution || [];
    if (!rows.length) {
      return [{ name: 'No payments', value: 1, color: alpha('#94a3b8', 0.45) }];
    }
    return rows.map((row, index) => ({
      name: row.name || row.label || 'Other',
      value: Number(row.value || 0),
      color: METHOD_COLORS[index % METHOD_COLORS.length],
    }));
  }, [charts.payment_method_distribution]);

  const revenueTrend = charts.daily_revenue_trend || charts.daily_booking_trend || [];

  return (
    <Paper
      elevation={0}
      sx={{
        ...surfaceSx,
        mb: 3,
        p: { xs: 1.75, sm: 2.25 },
        borderRadius: 2.5,
        background: isDark
          ? `linear-gradient(145deg, ${alpha('#0b1524', 0.98)} 0%, ${alpha('#0f172a', 0.98)} 100%)`
          : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
      }}
    >
      <Stack spacing={2.25}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          spacing={1.5}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: HALL_ACCENT,
                fontWeight: 900,
                letterSpacing: '0.12em',
              }}
            >
              Hall Performance
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: isDark ? '#f8fafc' : '#0f172a',
                fontWeight: 900,
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
              }}
            >
              {hallName || 'Hall'} analytics
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mt: 0.35 }}>
              Events, revenue, profit, and occupancy for this venue.
            </Typography>
          </Box>
          {typeof onDateRangeChange === 'function' ? (
            <DashboardReportHeader
              actionsOnly
              showPdf={false}
              startDate={startDate}
              endDate={endDate}
              onDateRangeChange={onDateRangeChange}
              onRefresh={onRefresh}
              loading={loading}
              themeMode={themeMode}
              accentColor={HALL_ACCENT}
              initialPreset={preset}
            />
          ) : null}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(6, minmax(0, 1fr))',
            },
            gap: 1.35,
          }}
        >
          {metricTiles.map((metric) => (
            <MetricTile
              key={metric.key}
              title={metric.title}
              value={metric.value}
              subtitle={metric.subtitle}
              icon={metric.icon}
              color={metric.color}
              isDark={isDark}
              formatValue={metric.formatValue}
            />
          ))}
        </Box>

        <DashboardChartCard
          variant="performance"
          eyebrow="REVENUE TREND"
          title=""
          subtitle={rangeLabel || 'Selected period revenue'}
          data={revenueTrend}
          chartType="area"
          showArea
          height={chartHeight}
          dataKeys={[{ dataKey: 'revenue', name: 'Revenue', color: SECONDARY }]}
          theme={themeMode}
          loading={loading}
          formatValue={formatCurrency}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <DashboardChartCard
            variant="performance"
            eyebrow="MONEY MIX"
            title=""
            subtitle="Revenue · Collection · Profit · Due"
            chartType="pie"
            pieData={moneyPie}
            height={chartHeight}
            theme={themeMode}
            loading={loading}
            formatValue={formatCurrency}
          />
          <DashboardChartCard
            variant="performance"
            eyebrow="PAYMENT METHOD"
            title=""
            subtitle={
              (charts.payment_method_distribution || []).length
                ? 'Collections by payment mode'
                : 'No payments in this range yet'
            }
            chartType="pie"
            pieData={paymentMethodPie}
            height={chartHeight}
            theme={themeMode}
            loading={loading}
            formatValue={formatCurrency}
          />
        </Box>

        {(charts.payment_method_distribution || []).length > 0 ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {charts.payment_method_distribution.map((row, index) => (
              <Chip
                key={`${row.method || row.name}-${index}`}
                size="small"
                label={`${row.name || row.label}: ${formatCurrency(row.value)} · ${row.count || 0}`}
                sx={{
                  fontWeight: 700,
                  color: isDark ? '#e2e8f0' : '#0f172a',
                  bgcolor: alpha(METHOD_COLORS[index % METHOD_COLORS.length], isDark ? 0.18 : 0.14),
                  border: `1px solid ${alpha(METHOD_COLORS[index % METHOD_COLORS.length], 0.35)}`,
                }}
              />
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}
